import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown, ArrowUpRight } from "lucide-react";
import { EASE, Overline, Reveal } from "@/components/motion";

const APP_URL = "https://app.uzakyonetim.com.tr";

const PLANS = [
  { slug: "baslangic", name: "Başlangıç", devices: "15 cihaz", monthly: "₺149", yearly: "₺1.490", featured: false },
  { slug: "kobi", name: "KOBİ", devices: "50 cihaz", monthly: "₺349", yearly: "₺3.490", featured: true },
  { slug: "kobi-plus", name: "KOBİ+", devices: "150 cihaz", monthly: "₺749", yearly: "₺7.490", featured: false },
  { slug: "kurumsal", name: "Kurumsal", devices: "Sınırsız cihaz", monthly: null, yearly: null, featured: false },
];

const COMMON_FEATURES = [
  "Uzak masaüstü erişimi",
  "Dosya transferi",
  "Terminal / Komut satırı",
  "Yazılım yönetimi",
  "Etkinlik logları",
  "2FA güvenlik",
  "Türkçe arayüz ve destek",
];

const ENTERPRISE_FEATURES = [
  "Sınırsız cihaz",
  "Öncelikli destek",
  "SLA garantisi",
  "Özel onboarding",
];

const FAQ = [
  {
    q: "Kredi kartı gerekiyor mu?",
    a: "Hayır. 14 günlük deneme sürecinizde herhangi bir ödeme bilgisi gerekmez. Süre sonunda devam etmek isterseniz ödeme yaparsınız.",
  },
  {
    q: "Cihaz limiti aşılırsa ne olur?",
    a: "Yeni cihaz ekleyemezsiniz. Sistem sizi otomatik olarak bir üst pakete yönlendirir, mevcut cihazlarınız çalışmaya devam eder.",
  },
  {
    q: "İstediğim zaman iptal edebilir miyim?",
    a: "Evet. Aylık planda istediğiniz zaman iptal edebilirsiniz. Yıllık planda kalan süreniz devam eder, iade yapılmaz.",
  },
  {
    q: "Yıllık paketten aylığa geçebilir miyim?",
    a: "Yıllık paket süreniz dolduğunda aylık pakete geçiş yapabilirsiniz.",
  },
  {
    q: "Paket yükseltebilir miyim?",
    a: "Evet, istediğiniz zaman daha üst pakete geçebilirsiniz. Kalan süreniz yeni paketinize aktarılır.",
  },
  {
    q: "Verilerim güvende mi?",
    a: "Verileriniz güvenli sunucularda saklanır, SSL ile şifrelenir, üçüncü taraflarla paylaşılmaz.",
  },
  {
    q: "Teknik destek nasıl çalışıyor?",
    a: "E-posta üzerinden Türkçe destek sunuyoruz. Kurumsal planda öncelikli destek ve SLA garantisi mevcuttur.",
  },
];

function FaqItem({ item, index, open, onToggle }) {
  const isOpen = open === index;
  return (
    <div className="border-b border-black/5" data-testid={`faq-item-${index}`}>
      <button
        data-testid={`faq-button-${index}`}
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
      >
        <span className="font-display text-base font-semibold tracking-tight text-[#0A0A0A] md:text-lg">
          {item.q}
        </span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-[#003366] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
          strokeWidth={1.5}
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="max-w-3xl pb-6 text-sm leading-relaxed text-[#525252] md:text-base">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Pricing() {
  const [yearly, setYearly] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <main data-testid="pricing-page" className="pb-24 pt-36 md:pb-32 md:pt-44">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="mx-auto mb-14 max-w-2xl text-center md:mb-20">
          <Reveal>
            <Overline>Fiyatlandırma</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display text-4xl font-bold tracking-tighter text-[#0A0A0A] sm:text-5xl lg:text-6xl">
              İhtiyacınıza göre
              <br />
              <span className="text-[#003366]">esnek planlar.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base leading-relaxed text-[#525252] md:text-lg">
              14 gün ücretsiz deneyin, kredi kartı gerekmez. İstediğiniz zaman
              paket değiştirin veya iptal edin.
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-10 inline-flex items-center rounded-full border border-black/10 bg-white p-1.5">
              <button
                data-testid="pricing-toggle-monthly"
                onClick={() => setYearly(false)}
                className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                  !yearly ? "bg-[#003366] text-white" : "text-[#525252] hover:text-[#0A0A0A]"
                }`}
              >
                Aylık
              </button>
              <button
                data-testid="pricing-toggle-yearly"
                onClick={() => setYearly(true)}
                className={`flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                  yearly ? "bg-[#003366] text-white" : "text-[#525252] hover:text-[#0A0A0A]"
                }`}
              >
                Yıllık
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    yearly ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"
                  }`}
                >
                  2 ay bedava
                </span>
              </button>
            </div>
          </Reveal>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((plan, i) => (
            <Reveal key={plan.slug} delay={i * 0.08}>
              <article
                data-testid={`plan-card-${plan.slug}`}
                className={`relative flex h-full flex-col rounded-3xl border bg-white p-8 transition-transform duration-500 hover:-translate-y-1.5 ${
                  plan.featured
                    ? "border-[#003366] shadow-[0_24px_60px_-24px_rgba(0,51,102,0.35)]"
                    : "border-black/5"
                }`}
              >
                {plan.featured && (
                  <span
                    data-testid="plan-badge-onerilen"
                    className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-[#003366] px-4 py-1 text-xs font-bold uppercase tracking-widest text-white"
                  >
                    Önerilen
                  </span>
                )}

                <h2 className="font-display text-xl font-bold tracking-tight text-[#0A0A0A]">
                  {plan.name}
                </h2>
                <p className="mt-1 text-sm font-medium text-[#525252]">{plan.devices}</p>

                <div className="mt-6 flex items-end gap-2">
                  {plan.monthly ? (
                    <>
                      <AnimatePresence mode="wait" initial={false}>
                        <motion.span
                          key={yearly ? "y" : "m"}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -12 }}
                          transition={{ duration: 0.35, ease: EASE }}
                          data-testid={`plan-price-${plan.slug}`}
                          className="font-display text-4xl font-bold tracking-tighter text-[#0A0A0A]"
                        >
                          {yearly ? plan.yearly : plan.monthly}
                        </motion.span>
                      </AnimatePresence>
                      <span className="pb-1 text-sm text-[#525252]">
                        {yearly ? "/yıl" : "/ay"}
                      </span>
                    </>
                  ) : (
                    <span
                      data-testid={`plan-price-${plan.slug}`}
                      className="font-display text-4xl font-bold tracking-tighter text-[#0A0A0A]"
                    >
                      Teklif Al
                    </span>
                  )}
                </div>

                {yearly && plan.monthly && (
                  <span className="mt-2 inline-flex w-fit rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
                    2 ay bedava
                  </span>
                )}

                <ul className="mt-8 flex-1 space-y-3">
                  {(plan.slug === "kurumsal" ? ENTERPRISE_FEATURES : COMMON_FEATURES).map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[#525252]">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#003366]" strokeWidth={2} />
                      {f}
                    </li>
                  ))}
                  {plan.slug === "kurumsal" && (
                    <li className="pt-2 text-xs italic text-[#9CA3AF]">
                      + tüm standart özellikler dahildir
                    </li>
                  )}
                </ul>

                {plan.slug === "kurumsal" ? (
                  <a
                    href="/#iletisim"
                    data-testid={`plan-cta-${plan.slug}`}
                    className="mt-8 inline-flex items-center justify-center gap-2 rounded-full border border-[#003366]/25 px-6 py-3.5 text-sm font-semibold text-[#003366] transition-colors duration-300 hover:bg-[#003366] hover:text-white"
                  >
                    Teklif Al
                  </a>
                ) : (
                  <a
                    href={APP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`plan-cta-${plan.slug}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-colors duration-300 ${
                      plan.featured
                        ? "bg-[#003366] text-white hover:bg-[#336699]"
                        : "border border-[#003366]/25 text-[#003366] hover:bg-[#003366] hover:text-white"
                    }`}
                  >
                    14 Gün Ücretsiz Dene
                    <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                  </a>
                )}
              </article>
            </Reveal>
          ))}
        </div>

        <div className="mx-auto mt-24 max-w-3xl md:mt-32">
          <Reveal>
            <Overline>Sık Sorulan Sorular</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tighter text-[#0A0A0A] sm:text-4xl">
              Aklınıza takılanlar
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div data-testid="faq-list" className="mt-10 border-t border-black/5">
              {FAQ.map((item, i) => (
                <FaqItem
                  key={item.q}
                  item={item}
                  index={i}
                  open={openFaq}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
